// Import nécessaires inchangés
import { createWretchInstance } from '../utils/fetch'
import { setCsrfToken  } from '../features/auth/authSlice'
import { createAsyncThunk } from '@reduxjs/toolkit'
import { selectCsrfToken } from '../store/selectors'

import { addFlashMessage } from '../features/flash/flashMessagesSlice'

const baseUrl = '/api/v1/'

const csrfTokenMiddleware = (token) => next => (url, opts) => {
  if(['POST', 'PUT', 'DELETE'].includes(opts.method)) {
    opts.headers['x-csrf-token'] = token
    if (!(opts.body instanceof FormData)) {
      opts.headers['Content-Type'] = 'application/json'
    }
  }
  return next(url, opts)
}

/**
 * Extrait le message d'erreur approprié à partir de l'objet WretchError
 * @param {WretchError} error - L'objet d'erreur retourné par wretch
 * @returns {string} Le message d'erreur formaté
 */
/**
 * Format Rails JSON: { base: string|string[], errors: [{ field: "message" }, ...] }
 */
const getErrorMessage = (error) => {
  if (error && error.json) {
    const { base, errors } = error.json

    // Normalise `base` en tableau de strings
    const baseMessages = Array.isArray(base)
      ? base.filter(Boolean)
      : base && typeof base === 'string'
        ? [base]
        : []

    // Normalise `errors` : tableau d'objets { field: "message" }
    const fieldMessages = Array.isArray(errors)
      ? errors.flatMap(obj =>
          Object.entries(obj).map(([field, msg]) =>
            field === 'base' ? msg : `${field} ${msg}`
          )
        )
      : []

    const all = [...baseMessages, ...fieldMessages].filter(Boolean)
    if (all.length > 0) return all.join(', ')
  }

  if (error.text && error.text.length < 300) {
    return error.text
  }

  return error.message || 'Une erreur inconnue est survenue'
}

export class ApiError extends Error {
  constructor(message, data = {}) {
    super(message)
    this.name = 'ApiError'
    this.data = data
  }
}

const api = (csrfToken, dispatch) => createWretchInstance({}, baseUrl)
  .middlewares([csrfTokenMiddleware(csrfToken)])
  .catcher(400, (error) => {
    const errorMsg = getErrorMessage(error)
    const userMsg = errorMsg && errorMsg.length < 300
      ? errorMsg
      : 'La requête est invalide. Vérifiez les données saisies.'
    dispatch(addFlashMessage({ type: 'error', text: userMsg }))
    throw new ApiError("Bad Request", error.json)
  })
  .catcher(401, async (error, request) => {
    console.error('Unauthorized:', error);
    const errorMsg = getErrorMessage(error);
    dispatch(addFlashMessage({ type: 'error', text: 'Vous n\'êtes pas autorisé à accéder à cette ressource.' }));
    throw new Error(errorMsg);
  })
  .catcher(403, (error, request) => {
    console.error('Forbidden:', error);
    const errorMsg = getErrorMessage(error);
    dispatch(addFlashMessage({ type: 'error', text: 'Accès interdit.' }));
    throw new Error(errorMsg);
  })
  .catcher(404, (error, request) => {
    console.error('Not Found:', error);
    const msg = 'La ressource demandée n\'a pas été trouvée.';
    dispatch(addFlashMessage({ type: 'error', text: msg }));
    throw new Error(msg);
  })
  .catcher(422, async (error, request) => {
    console.error('Erreur 422:', error)
    const errorMsg = getErrorMessage(error)
    const userMsg = errorMsg && errorMsg.length < 300
      ? errorMsg
      : 'Les données soumises sont invalides.'
    dispatch(addFlashMessage({ type: 'error', text: userMsg }))
    throw new ApiError("Unprocessable Content", error.json)
  })
  .catcher(500, (error, request) => {
    console.error('Erreur serveur:', error);
    const errorMsg = getErrorMessage(error);
    dispatch(addFlashMessage({ type: 'error', text: 'Une erreur est survenue sur le serveur. Veuillez réessayer plus tard.' }));
    throw new Error(errorMsg);
  })
  // Ajout d'un catcher générique pour les autres codes d'erreur
  .catcherFallback(error => {
    // Le code 304 Not Modified n'est pas une erreur, on le laisse passer
    if (error.status === 304) {
      return null
    }
    
    console.error(`Erreur ${error.status}:`, error);
    const errorMsg = getErrorMessage(error);
    dispatch(addFlashMessage({ type: 'error', text: `${errorMsg}` }));
    throw new Error(errorMsg);
  })
  .resolve(resolver => {
    return resolver.res(async (response) => {
      const csrf = response.headers.get('x-csrf-token')
      if(csrf) {
        dispatch(setCsrfToken(csrf))
      }
      
      // Vérifier si le corps de la réponse est vide
      const contentType = response.headers.get('content-type')
      const contentLength = response.headers.get('content-length')
      
      // Si content-length est 0 ou si content-type n'indique pas de JSON, ne pas parser
      if (contentLength === '0' || !contentType || !contentType.includes('application/json')) {
        return null
      }
      
      // Vérifier si le corps est vide en clonant la réponse et en lisant le texte
      const clonedResponse = response.clone()
      const text = await clonedResponse.text()
      if (!text || text.trim() === '') {
        return null
      }
      
      // Si le corps n'est pas vide, parser en JSON
      return response.json()
    })
  })

export const createApiAsyncThunk = (type, payloadCreator) => createAsyncThunk(
  type,
  async (arg, thunkAPI) => {
    const { getState, dispatch, rejectWithValue } = thunkAPI
    const csrfToken = selectCsrfToken(getState())
    const apiInstance = api(csrfToken, dispatch)

    try {
      return await payloadCreator(arg, { ...thunkAPI, api: apiInstance });
    } catch (error) {
      console.log(error)
      return rejectWithValue(error.message);
    }
  }
)

export default api
